import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Vimeo from '@u-wave/react-vimeo';
import Image from '../Image';
import Button from '../Button';
import i18n from '../../i18n';
import classNames from 'classnames';
import styles from './VideoModule.module.scss';
import { getApprovedCookieTargets } from '../../utils/cookie';
import CookieBlocker from '../CookieBlocker';
import { getCookie } from '../../utils/cookie';

const getVideoProvider = (url) => {
    if (url.indexOf('vimeo.com') > -1) {
        return 'vimeo';
    } else if (url.indexOf('mediaflow.com') > -1) {
        return 'mediaflow';
    } else if (url.indexOf('mediaflowpro.com') > -1) {
        return 'mediaflow';
    } else {
        return '';
    }
};

const VideoModule = ({
    playVideo,
    overlayImage,
    videoUrl,
    descriptiveText,
}) => {
    const [videoProvider, setVideoProvider] = useState('');
    const [videoError, setVideoError] = useState({
        hasError: false,
        message: '',
    });

    const targets = getApprovedCookieTargets();
    const hasEnabledCookies = getCookie('enableTracking');
    const vimeoApproved = targets.accept.includes('targeting');
    const mediaflowApproved = targets.accept.includes('performance');
    const setError = (message) =>
        setVideoError({
            hasError: true,
            message,
        });

    useEffect(() => {
        const provider = getVideoProvider(videoUrl);

        if (provider) {
            setVideoProvider(provider);
        } else {
            setError(
                `Unknown provider in video url "${videoUrl}". Only urls from Vimeo or MediaFlow beginning with "vimeo.com" or "mediaflow.com are supported`
            );
        }
    }, [videoUrl]);

    return (
        <>
            {videoProvider === 'vimeo' && (
                <>
                    {vimeoApproved || hasEnabledCookies ? (
                        <VideoModuleVimeo
                            playVideo={playVideo}
                            overlayImage={overlayImage}
                            videoUrl={videoUrl}
                            videoProvider={videoProvider}
                            descriptiveText={descriptiveText}
                        />
                    ) : (
                        <CookieBlocker type="Video" url={videoUrl} />
                    )}
                </>
            )}
            {videoProvider === 'mediaflow' && (
                <>
                    {mediaflowApproved || hasEnabledCookies ? (
                        <VideoModuleMediaFlow
                            playVideo={playVideo}
                            overlayImage={overlayImage}
                            videoUrl={videoUrl}
                            descriptiveText={descriptiveText}
                        />
                    ) : (
                        <CookieBlocker
                            type="Video"
                            provider={videoProvider}
                            url={videoUrl}
                        />
                    )}
                </>
            )}
            {videoError.hasError && (
                <div className={styles['VideoModule__Video__Error']}>
                    {videoError.message}
                </div>
            )}
        </>
    );
};

VideoModule.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    overlayImage: PropTypes.object,
    descriptiveText: PropTypes.string,
};

VideoModule.defaultProps = {
    videoUrl: '',
    descriptiveText: '',
    image: {},
};

const VideoModuleVimeo = ({
    playVideo,
    overlayImage,
    videoUrl,
    descriptiveText,
}) => {
    const videoPlayer = useRef(null);
    const videoRef = useRef(null);
    const overlayRef = useRef(null);

    const [videoInited, setVideoInited] = useState(false);

    useEffect(() => {
        if (videoRef.current && overlayRef.current) {
            let vref = window.getComputedStyle(videoRef.current);

            const checking = setInterval(() => {
                vref = window.getComputedStyle(videoRef.current);
                overlayRef.current.style.height = `calc(${vref.height} - ${vref.paddingBottom}`;

                if (vref.height !== '0px') {
                    clearInterval(checking);
                }
            }, 100);
        }
    }, [videoUrl]);

    const handlePlayVideoClick = () => {
        // hide button + overlay & show player iframe
        setVideoInited(true);

        videoPlayer.current.play();
    };

    const handleVimeoPlayerReady = (playerControls) => {
        videoPlayer.current = playerControls;
    };

    return (
        <div className={styles['VideoModule']}>
            <div ref={overlayRef} className={styles['VideoModule__Container']}>
                {!videoInited && !playVideo && (
                    <div className={styles['VideoModule__ImageOverlay']}>
                        <Image {...overlayImage} />
                    </div>
                )}

                <div ref={videoRef} className={styles['VideoModule__Video']}>
                    <Vimeo
                        video={videoUrl}
                        onReady={handleVimeoPlayerReady}
                        responsive={true}
                        background={false}
                        showTitle={false}
                        showByline={false}
                        volume={0.3}
                        autoplay={playVideo}
                        controls={true}
                    />
                </div>

                {!videoInited && !playVideo && (
                    <div className={styles['VideoModule__Button']}>
                        <Button
                            modifier={'VideoModule'}
                            label={
                                descriptiveText
                                    ? i18n.t(
                                          'videoModule.playDescriptive',
                                          descriptiveText
                                      )
                                    : i18n.t('videoModule.play')
                            }
                            onClick={handlePlayVideoClick}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const VideoModuleMediaFlow = ({
    playVideo,
    overlayImage,
    videoUrl,
    descriptiveText,
}) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    let [mfPlayerInstance, setMfPlayerInstance] = useState(null);
    const [videoInited, setVideoInited] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const extractVideoId = (videoUrl) => {
        const urlParts = videoUrl.split('/');
        const videoId = urlParts[urlParts.length - 1];

        return videoId;
    };
    const [elements, setElements] = useState();
    const [elementsAreSet, setElementsAreSet] = useState(false);
    const [videoIsPlaying, setVideoIsPlaying] = useState(false);

    useEffect(() => {
        if (videoRef.current) {
            let vref = window.getComputedStyle(videoRef.current);

            const checking = setInterval(() => {
                containerRef.current.style.height = vref.height;

                if (parseFloat(vref.height) > 0 && playerRef.current) {
                    setTimeout(() => {
                        containerRef.current.style.height = vref.height;
                        clearInterval(checking);
                        setLoaded(true);
                    }, 700);
                }
            }, 300);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
        /* eslintyy-disable react-hooks/exhaustive-deps */
    }, []);

    useEffect(() => {
        const loadPlayer = async () => {
            const videoId = extractVideoId(videoUrl);

            const cssLoaded = !!document.querySelector(
                'link[href="https://mfstatic.com/css/mediaflowplayer.min.css"]'
            );

            if (!cssLoaded) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.type = 'text/css';
                link.href = 'https://mfstatic.com/css/mediaflowplayer.min.css';
                document.head.appendChild(link);
            }

            const mfPlayerDefined = typeof MFPlayer !== 'undefined';

            if (!mfPlayerDefined) {
                const script = document.createElement('script');
                script.crossOrigin = 'anonymous';
                script.src = 'https://mfstatic.com/js/mediaflowplayer.min.js';
                await new Promise((resolve) => {
                    script.onload = resolve;
                    document.body.appendChild(script);
                });
            }

            if (playerRef.current) {
                // eslint-disable-next-line no-undef
                const player = new MFPlayer(playerRef.current, videoId);

                if (player !== undefined) {
                    setMfPlayerInstance(player);
                }
            }
        };

        loadPlayer();
    }, [videoUrl]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            handleResize();
        });

        if (playerRef.current) {
            resizeObserver.observe(playerRef.current);
        }

        if (videoRef.current) {
            resizeObserver.observe(videoRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const handleKeyDown = (event) => {
        if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            handlePlayVideoClick();
        }
    };

    const handleSpaceBar = (event) => {
        if (event.code === 'Space') {
            checkPlayerStatus();
            setVideoIsPlaying(!videoIsPlaying);
        }
    };
    const handleButtonPlayClick = (e) => {
        if (!mfPlayerInstance || !playerRef.current) {
            return;
        }

        if (e && e.type === 'click') {
            e.stopPropagation();
            setVideoIsPlaying(!videoIsPlaying);
            checkPlayerStatus();

            return;
        }
    };

    const handleButtonEnter = (e) => {
        if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            e.stopPropagation();
            setVideoIsPlaying(!videoIsPlaying);
            checkPlayerStatus();
            return;
        }
    };

    const handleButtonSpaceBar = (e) => {
        if (e.code === 'Space') {
            e.stopPropagation();
            checkPlayerStatus();
            setVideoIsPlaying(!videoIsPlaying);

            return;
        }
    };

    const handlePlayVideoClick = (e) => {
        if (!mfPlayerInstance || !playerRef.current) {
            return;
        }

        const { player } = mfPlayerInstance;

        if (e && e.type === 'click') {
            setVideoIsPlaying(!videoIsPlaying);
            player.togglePlay();
            return;
        }

        player.togglePlay();
        setVideoIsPlaying(!videoIsPlaying);
        checkPlayerStatus();
    };

    const checkPlayerStatus = () => {
        if (!mfPlayerInstance) {
            return;
        }

        const { player } = mfPlayerInstance;
        player.on('play', () => {
            setTabFocusForPlayer();
            return;
        });

        player.on('waiting', () => {
            removeTabFocusForPlayer();
            return;
        });
        player.on('playing', () => {
            setTabFocusForPlayer();
            return;
        });

        player.on('pause', () => {
            setTabFocusForPlayer;
            return;
        });

        if (player.ready === true) {
            removeTabFocusForPlayer();
        }

        return;
    };

    const setTabFocusForPlayer = () => {
        elements.forEach((el) => {
            if (el !== playerRef.current) {
                el.tabIndex = 0;
            }
        });
    };

    const removeTabFocusForPlayer = () => {
        elements.forEach((el) => {
            el.tabIndex = -1;
        });
    };

    const handleElements = async () => {
        if (!playerRef.current) {
            return;
        }

        let extractedElements = Array.from(
            playerRef.current.querySelectorAll('*')
        ).filter((el) => el.tabIndex === 0 && !el.classList.contains('plyr'));

        if (extractedElements !== undefined && extractedElements.length > 0) {
            setElements(extractedElements);

            setElementsAreSet(true);
            handleVideoInit();
            return;
        } else {
            setTimeout(() => {
                handleElements();
            }, 700);
        }

        return;
    };

    const handleVideoInit = () => {
        if (!elementsAreSet) {
            playerRef.current.tabIndex = -1;
            setVideoInited(true);
            return;
        }
    };

    const handleResize = () => {
        let vref = window.getComputedStyle(videoRef.current);
        containerRef.current.style.height = vref.height;
    };

    useEffect(() => {
        if (!mfPlayerInstance && !playerRef.current) {
            return;
        }

        if (videoInited) {
            playerRef.current.addEventListener('keydown', handleKeyDown);
            playerRef.current.addEventListener('keyup', handleSpaceBar);
            Array.from(playerRef.current.querySelectorAll('*')).forEach(
                (el) => {
                    if (el.getAttribute('data-plyr') === 'play') {
                        el.addEventListener('click', handleButtonPlayClick);
                        el.addEventListener('keydown', handleButtonEnter);
                        el.addEventListener('keyup', handleButtonSpaceBar);
                    }
                }
            );

            checkPlayerStatus();
            return;
        }

        if (!videoInited) {
            handleElements();
            return;
        }

        return () => {
            playerRef.current.removeEventListener('keydown', handleKeyDown);
            playerRef.current.removeEventListener('keyup', handleSpaceBar);
            Array.from(playerRef.current.querySelectorAll('*')).forEach(
                (el) => {
                    if (el.getAttribute('data-plyr') === 'play') {
                        el.removeEventListener('click', handleButtonPlayClick);
                    }
                }
            );
        };
    }, [videoInited]);

    const containerStylesMF = classNames(
        styles['VideoModule'],
        styles['VideoModule--MediaFlow'],
        {
            [styles['VideoModule--MediaFlow--Inited']]: videoInited && loaded,
        }
    );

    return (
        <div className={containerStylesMF}>
            <div
                ref={containerRef}
                className={styles['VideoModule__Container']}
                tabIndex={-1}
            >
                {videoInited &&
                    !playVideo &&
                    !!overlayImage.file &&
                    !videoIsPlaying && (
                        <div
                            className={styles['VideoModule__ImageOverlay']}
                            onClick={handlePlayVideoClick}
                            tabIndex={-1}
                        >
                            <Image {...overlayImage} />
                        </div>
                    )}
                <div ref={videoRef} className={styles['VideoModule__Video']}>
                    <div
                        className={styles['VideoModule__MFP']}
                        ref={playerRef}
                        tabIndex={-1}
                    ></div>
                </div>
                {videoInited &&
                    !playVideo &&
                    !!overlayImage.file &&
                    !videoIsPlaying && (
                        <div className={styles['VideoModule__Button']}>
                            <Button
                                modifier={'VideoModule'}
                                label={
                                    descriptiveText
                                        ? i18n.t(
                                              'videoModule.playDescriptive',
                                              descriptiveText
                                          )
                                        : i18n.t('videoModule.play')
                                }
                                onClick={handlePlayVideoClick}
                            />
                        </div>
                    )}
            </div>
        </div>
    );
};
export default VideoModule;

VideoModuleVimeo.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    overlayImage: PropTypes.object,
    descriptiveText: PropTypes.string,
    playVideo: PropTypes.bool,
};
VideoModuleVimeo.defaultProps = {
    videoUrl: '',
    descriptiveText: '',
    image: {},
    playVideo: false,
};

VideoModuleMediaFlow.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    overlayImage: PropTypes.object,
    descriptiveText: PropTypes.string,
    playVideo: PropTypes.bool,
};
VideoModuleMediaFlow.defaultProps = {
    videoUrl: '',
    descriptiveText: '',
    image: {},
    playVideo: false,
};

VideoModule.propTypes = {
    videoUrl: PropTypes.string.isRequired,
    overlayImage: PropTypes.object,
    descriptiveText: PropTypes.string,
    playVideo: PropTypes.bool,
};
VideoModule.defaultProps = {
    videoUrl: '',
    descriptiveText: '',
    image: {},
    playVideo: false,
};
